import { SelectedDate } from '../CalenderComponent';
import { useMutation as useApolloMutation } from '@apollo/client';
import {
  GET_CAR,
  UPDATE_PRICE_CALENDAR,
  UPDATE_HOURLY_PRICE_CALENDAR,
} from '../../../services/carService/queries';
import { IUpdatePriceCalendarMutation } from '../../../services/carService/__generated__/queries.generated';
import tw from 'twin.macro';
import styled from 'styled-components';
import { Dispatch, SetStateAction, useState } from 'react';
import { toast } from 'react-toastify';
import NumericInput from '../../NumericInput';

/** Upper bound for a daily or hourly price, same as the Flutter host calendar. */
const PRICE_MAX = 9999;

const Panel = styled.div<{ $active: boolean; $borderColor?: string }>`
  ${tw`border-solid border-2 rounded-card p-5 mb-4 cursor-pointer transition-all bg-white`}
  ${({ $active, $borderColor }) =>
    $active
      ? `border-color: ${$borderColor || '#60a5fa'}; shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);`
      : tw`border-border-default`}
  &:hover {
    ${tw`border-border-default`}
  }
`;

const Title = styled.h2`
  ${tw`text-lg font-bold mb-1 text-text-primary`}
`;

const Description = styled.p`
  ${tw`text-sm text-text-secondary mb-4 leading-relaxed`}
`;

const InputContainer = styled.div`
  ${tw`flex flex-col gap-4 mt-4`}
`;

const ToggleGroup = styled.div`
  ${tw`flex bg-bg-secondary p-1 rounded-lg w-fit`}
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  ${tw`px-8 py-2 text-sm font-bold rounded-md transition-all`}
  ${({ $active }) => ($active ? tw`bg-black text-white shadow-card` : tw`text-text-secondary`)}

  &:hover {
    ${({ $active }) => !$active && tw`text-text-primary`}
  }
`;

const InputGroup = styled.div`
  ${tw`flex items-center gap-2`}
`;

const CurrencySymbol = styled.div`
  ${tw`flex items-center justify-center bg-bg-secondary border border-border-default border-r-0 rounded-l-lg h-12 w-12 text-text-secondary font-medium`}
`;

const Input = styled(NumericInput)`
  ${tw`flex-1 h-12 px-4 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-black transition-all text-text-primary`}
`;

const ViewOnCalendarButton = styled.button`
  ${tw`w-full py-3 bg-bg-secondary rounded-lg text-text-secondary font-bold transition-all mt-2`}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const UpdateButton = styled.button`
  ${tw`w-full py-4 bg-black text-white rounded-card font-bold text-lg transition-all shadow-card-hover active:scale-[0.98]`}
  &:hover {
    ${tw`bg-black`}
  }
`;

const InsightsCard = styled.div`
  ${tw`bg-bg-secondary rounded-card p-4 mb-6 flex items-start gap-3 border border-border-default`}
`;

const InsightsIcon = styled.div`
  ${tw`text-black mt-1`}
`;

const InsightsText = styled.p`
  ${tw`text-sm text-text-secondary flex-1 leading-snug`}
`;

const ViewInsightsButton = styled.button`
  ${tw`bg-white border border-border-default px-4 py-2 rounded-lg text-sm font-bold text-text-primary transition-all whitespace-nowrap`}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

interface PriceAdjustmentPanelProps {
  selectedDates: SelectedDate[];
  setSelectedDates: Dispatch<SetStateAction<SelectedDate[]>>;
  carId: string;
  modeLabel?: 'daily' | 'hourly';
  priceCalendar?: Array<{ date: string; price: number; hourPrice?: number }>;
  onViewOnCalendar?: () => void;
  onUpdated?: () => void;
  onUpdatingDatesChange?: (dates: Set<string>) => void;
}

const PriceAdjustmentPanel: React.FC<PriceAdjustmentPanelProps> = ({
  selectedDates,
  setSelectedDates,
  carId,
  modeLabel = 'daily',
  priceCalendar = [],
  onViewOnCalendar,
  onUpdated,
  onUpdatingDatesChange,
}) => {
  const [mode, setMode] = useState<'adjust' | 'set'>('adjust');
  const [adjustUnit, setAdjustUnit] = useState<'$' | '%'>('$');
  const [updatingDates, setUpdatingDates] = useState<Set<string>>(new Set());

  const mutationDoc = modeLabel === 'hourly' ? UPDATE_HOURLY_PRICE_CALENDAR : UPDATE_PRICE_CALENDAR;

  const [updatePriceCalendar, { loading: updating }] =
    useApolloMutation<IUpdatePriceCalendarMutation>(mutationDoc, {
      refetchQueries: [
        {
          query: GET_CAR,
          variables: {
            id: carId,
          },
        },
      ],
      onCompleted: () => {
        setSelectedDates([]);
        toast.success('Prices updated successfully');
        if (onUpdated) onUpdated();
      },
      onError: () => {
        toast.error('Error updating prices');
        const emptySet = new Set<string>();
        setUpdatingDates(emptySet); // Clear loading on error
        if (onUpdatingDatesChange) {
          onUpdatingDatesChange(emptySet);
        }
      },
    });

  const [adjustAmt, setAdjustAmt] = useState<number | null>(null);
  const [setToPrice, setSetToPrice] = useState<number | null>(null);

  const handleUpdatePrices = async () => {
    if (!selectedDates.length) {
      toast.info('Please choose dates you want to change price');
      return;
    }

    const priceMap = new Map(priceCalendar.map(item => [item.date, item.price]));

    let mutationData: any[] = [];
    const datesToUpdate = new Set<string>();
    let hasInvalidPrice = false;

    if (mode === 'adjust' && adjustAmt !== null) {
      mutationData = [];

      selectedDates.forEach(date => {
        datesToUpdate.add(date.date);
        const existingDailyPrice = priceMap.get(date.date) ?? 0;
        const currentPrice = modeLabel === 'hourly' ? Number(date.price) : Number(date.price);
        let newPrice = currentPrice;

        if (adjustUnit === '$') {
          newPrice = currentPrice + adjustAmt;
        } else {
          newPrice = currentPrice * (1 + adjustAmt / 100);
        }

        // Business rule: price must never be 0 or below
        if (newPrice <= 0) {
          hasInvalidPrice = true;
          return;
        }
        // Same cap as the Flutter host calendar.
        newPrice = Math.min(newPrice, PRICE_MAX);

        mutationData.push({
          date: date.date,
          ...(modeLabel === 'hourly'
            ? { hourPrice: Math.round(newPrice), price: existingDailyPrice }
            : { price: Math.round(newPrice) }),
          isAvailable: date.isAvailable,
        });
      });
    } else if (mode === 'set' && setToPrice !== null) {
      // Business rule: price must never be 0 or below
      if (setToPrice <= 0) {
        toast.error('You can never set a date to 0 or less per day.');
        return;
      }

      const cappedPrice = Math.round(Math.min(setToPrice, PRICE_MAX));
      mutationData = selectedDates.map(date => {
        datesToUpdate.add(date.date);
        const existingDailyPrice = priceMap.get(date.date) ?? 0;
        return {
          date: date.date,
          ...(modeLabel === 'hourly'
            ? { hourPrice: cappedPrice, price: existingDailyPrice }
            : { price: cappedPrice }),
          isAvailable: date.isAvailable,
        };
      });
    }

    if (hasInvalidPrice) {
      toast.error('You can never set a date to 0 or less per day.');
      return;
    }

    if (mutationData.length > 0) {
      // Set updating dates and start timer
      const datesSet = new Set(datesToUpdate);
      setUpdatingDates(datesSet);
      if (onUpdatingDatesChange) {
        onUpdatingDatesChange(datesSet);
      }

      const startTime = Date.now();
      const MIN_LOADING_TIME = 2000; // 2 seconds minimum

      try {
        await updatePriceCalendar({
          variables: {
            carId,
            updatePriceCalendarData: mutationData,
          },
        });

        // Calculate remaining time to meet minimum
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

        // Wait for remaining time if needed
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
      } catch (error) {
        // Error handling is done in onError callback
      } finally {
        // Clear updating dates after minimum time has passed
        const emptySet = new Set<string>();
        setUpdatingDates(emptySet);
        if (onUpdatingDatesChange) {
          onUpdatingDatesChange(emptySet);
        }
      }
    }
  };

  return (
    <div tw="pb-20">
      <Panel $active={mode === 'adjust'} $borderColor="#2563eb" onClick={() => setMode('adjust')}>
        <Title>Adjust</Title>
        <Description>
          Add or reduce all selected prices by a dollar or percentage amount.
        </Description>
        {mode === 'adjust' && (
          <InputContainer onClick={e => e.stopPropagation()}>
            <ToggleGroup>
              <ToggleButton $active={adjustUnit === '$'} onClick={() => setAdjustUnit('$')}>
                $
              </ToggleButton>
              <ToggleButton $active={adjustUnit === '%'} onClick={() => setAdjustUnit('%')}>
                %
              </ToggleButton>
            </ToggleGroup>
            <InputGroup>
              <CurrencySymbol>{adjustUnit}</CurrencySymbol>
              <Input
                allowNegative
                placeholder="E.g. +100, -100"
                value={adjustAmt}
                onValueChange={val => setAdjustAmt(val)}
              />
            </InputGroup>
            {onViewOnCalendar && (
              <ViewOnCalendarButton onClick={onViewOnCalendar}>
                View on calendar
              </ViewOnCalendarButton>
            )}
          </InputContainer>
        )}
      </Panel>

      <Panel $active={mode === 'set'} onClick={() => setMode('set')}>
        <Title>Set</Title>
        <Description>Set all selected prices to the same value.</Description>
        {mode === 'set' && (
          <InputContainer onClick={e => e.stopPropagation()}>
            <InputGroup>
              <CurrencySymbol>$</CurrencySymbol>
              <Input
                placeholder="E.g. 100"
                value={setToPrice}
                onValueChange={val => setSetToPrice(val)}
                minValue={0}
              />
            </InputGroup>
          </InputContainer>
        )}
      </Panel>

      {(mode === 'adjust' || mode === 'set') && (
        <UpdateButton disabled={updating} onClick={handleUpdatePrices}>
          {updating
            ? 'Updating...'
            : `Update ${selectedDates.length} ${selectedDates.length === 1 ? 'day' : 'days'}`}
        </UpdateButton>
      )}
    </div>
  );
};

export default PriceAdjustmentPanel;
